import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Home, Plus, X, Trash2, Star, MapPin } from "lucide-react";

const ROOF_TYPES = [
  ["flat_concrete", "Flat concrete"],
  ["sloped_metal", "Sloped metal"],
  ["sloped_tile", "Sloped tile"],
  ["sloped_asphalt", "Sloped asphalt"],
  ["sloped_other", "Sloped (other)"],
  ["ground_mount", "Ground mount"],
  ["other", "Other"],
];

export default function PropertiesTab({ userId }) {
  const [props, setProps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    api
      .get(`/properties?user_id=${userId}`)
      .then((r) => setProps(r.data))
      .catch(() => toast.error("Could not load properties"))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
  }, [userId]); // eslint-disable-line

  const remove = async (p) => {
    if (!confirm(`Delete ${p.label} (${p.code})?`)) return;
    try {
      await api.delete(`/properties/${p.property_id}`);
      toast.success("Property deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (loading) return <div className="text-sm text-[#94A3B8] py-4">Loading properties…</div>;

  return (
    <div data-testid="properties-tab">
      <div className="flex items-center justify-between mb-4">
        <p className="overline">Sites owned by this customer</p>
        <button
          onClick={() => {
            setEditing(null);
            setShowForm(true);
          }}
          className="btn-accent text-xs py-1.5 px-3 inline-flex items-center gap-1"
          data-testid="new-property-btn"
        >
          <Plus className="w-3.5 h-3.5" /> Add property
        </button>
      </div>

      {props.length === 0 ? (
        <div className="text-sm text-[#94A3B8] italic py-4">No properties yet. Add the customer's home or work site to link projects to it.</div>
      ) : (
        <ul className="space-y-2">
          {props.map((p) => (
            <li
              key={p.property_id}
              data-testid={`prop-${p.property_id}`}
              className="border border-[#E2E8F0] dark:border-slate-700 rounded-md p-4 flex items-start justify-between hover:border-[#C9A356] transition"
            >
              <div className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-md grid place-items-center ${p.is_primary ? "bg-[#FFFBF0] text-[#C9A356]" : "bg-slate-100 dark:bg-slate-800 text-[#475569]"}`}>
                  {p.is_primary ? <Star className="w-4 h-4 fill-current" /> : <Home className="w-4 h-4" />}
                </div>
                <div>
                  <div className="text-sm font-medium text-[#0A2540] dark:text-white flex items-center gap-2">
                    {p.label}
                    <span className="text-[10px] font-mono text-[#94A3B8]">{p.code}</span>
                    {p.is_primary && (
                      <span className="text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-[#C9A356]/15 text-[#8A6B14]">Primary</span>
                    )}
                  </div>
                  <div className="text-xs text-[#475569] dark:text-slate-400 mt-0.5 inline-flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> {p.address}
                    {p.city ? `, ${p.city}` : ""}
                  </div>
                  {p.roof_type && (
                    <div className="text-[10px] text-[#94A3B8] mt-1 capitalize">
                      {p.roof_type.replace(/_/g, " ")}
                      {p.size_kw_estimate ? ` · est ${p.size_kw_estimate} kW` : ""}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => {
                    setEditing(p);
                    setShowForm(true);
                  }}
                  className="text-xs text-[#475569] hover:text-[#0A2540] dark:hover:text-white px-2 py-1"
                  data-testid={`edit-prop-${p.property_id}`}
                >
                  Edit
                </button>
                <button
                  onClick={() => remove(p)}
                  className="text-xs text-red-600 hover:text-red-700 px-2 py-1 inline-flex items-center gap-1"
                  data-testid={`del-prop-${p.property_id}`}
                >
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {showForm && (
        <PropertyForm
          userId={userId}
          initial={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => {
            setShowForm(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function PropertyForm({ userId, initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    label: initial?.label || "",
    address: initial?.address || "",
    city: initial?.city || "",
    country: initial?.country || "St. Maarten",
    roof_type: initial?.roof_type || "",
    size_kw_estimate: initial?.size_kw_estimate || "",
    roof_size_m2: initial?.roof_size_m2 || "",
    is_primary: initial?.is_primary || false,
    notes: initial?.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.address.trim()) {
      toast.error("Address required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, user_id: userId };
      Object.keys(payload).forEach((k) => payload[k] === "" && delete payload[k]);
      if (initial) {
        await api.patch(`/properties/${initial.property_id}`, payload);
        toast.success("Property updated");
      } else {
        await api.post("/properties", payload);
        toast.success("Property added");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose} role="dialog" aria-modal="true">
      <div className="bg-white dark:bg-slate-900 rounded-xl p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="property-form">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl text-[#0A2540] dark:text-white">{initial ? "Edit property" : "Add property"}</h3>
          <button onClick={onClose}>
            <X className="w-5 h-5" />
          </button>
        </div>
        <style>{`.pb-input{width:100%;border:1px solid #E2E8F0;border-radius:6px;padding:8px 12px;font-size:14px;background:transparent}.dark .pb-input{border-color:#334155;background:#0F172A;color:#fff}`}</style>
        <div className="space-y-3">
          <Field label="Label" required>
            <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="prop-label" placeholder="Main house / Rental villa / Office" className="pb-input" />
          </Field>
          <Field label="Address" required>
            <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="prop-address" placeholder="12 Palm Beach Rd" className="pb-input" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="City">
              <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="prop-city" className="pb-input" />
            </Field>
            <Field label="Country">
              <input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="prop-country" className="pb-input" />
            </Field>
          </div>
          <Field label="Roof type">
            <select value={form.roof_type} onChange={(e) => setForm({ ...form, roof_type: e.target.value })} data-testid="prop-roof-type" className="pb-input">
              <option value="">—</option>
              {ROOF_TYPES.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Roof size (m²)">
              <input type="number" step="1" min="0" value={form.roof_size_m2} onChange={(e) => setForm({ ...form, roof_size_m2: e.target.value })} className="pb-input" />
            </Field>
            <Field label="Est. system size (kW)">
              <input type="number" step="0.1" min="0" value={form.size_kw_estimate} onChange={(e) => setForm({ ...form, size_kw_estimate: e.target.value })} className="pb-input" />
            </Field>
          </div>
          <Field label="Notes">
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="pb-input" placeholder="Gate code, access instructions, contact on site…" />
          </Field>
          <label className="inline-flex items-center gap-2 text-sm text-[#0A2540] dark:text-white">
            <input type="checkbox" checked={form.is_primary} onChange={(e) => setForm({ ...form, is_primary: e.target.checked })} data-testid="prop-primary" />
            Mark as primary property
          </label>
        </div>
        <div className="flex gap-2 justify-end mt-5">
          <button onClick={onClose} className="btn-outline-navy text-sm">
            Cancel
          </button>
          <button onClick={save} disabled={saving} className="btn-accent text-sm" data-testid="save-prop">
            {saving ? "Saving…" : initial ? "Save changes" : "Add property"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-[#475569] dark:text-slate-400 mb-1 font-bold">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
    </div>
  );
}